/** The music bed: four short pieces, written as chords rather than recorded.
 *
 * Same reasoning as `synth.ts` - there are no audio files - but the constraint
 * bites harder here, because a loop that repeats every eight seconds is worse
 * than silence. Three things keep it bearable:
 *
 *  1. The progressions are eight bars, not four, and the two halves differ.
 *  2. The pad voices are detuned against each other by a few cents, so the
 *     chord beats slowly and no two bars are acoustically identical.
 *  3. The melody is a walk over the chord tones driven by the bar number, so
 *     it changes every bar and comes back around only after sixteen.
 *
 * It is a bed, not a soundtrack. Everything here sits at least 12 dB under the
 * cues, and the whole thing is off by default until the player turns it on.
 */

import type { Patch, Voice } from "./synth.js";
import { note } from "./synth.js";

export const SCENES = ["menu", "adventure", "town", "battle"] as const;
export type Scene = (typeof SCENES)[number];

/**
 * A recorded piece for a scene. The host names the file; the engine streams
 * it through the music bus and loops it at `loopSeconds`, which must be the
 * exact wrap point - the file is expected to carry a little of its own
 * opening after that point, so the loop does not depend on how the browser's
 * decoder pads the tail. A scene with no track plays the synthesised bed.
 */
export interface Track {
  readonly url: string;
  readonly loopSeconds: number;
}

/**
 * Several recordings for one scene, played in turn: each runs a full pass and
 * hands over to the next, and the scene picks up from where it left off the
 * next time it is shown. One entry is a plain loop; none is the bed.
 */
export type Playlist = readonly Track[];

/** What the engine and the hook accept, as the list they work on. */
export function toPlaylist(tracks: Track | Playlist | null | undefined): Playlist {
  if (tracks === null || tracks === undefined) return [];
  return Array.isArray(tracks) ? (tracks as Playlist) : [tracks as Track];
}

export interface Score {
  /** The tonic, as a note name. Everything else is semitones from it. */
  readonly root: string;
  readonly secondsPerBar: number;
  /** Chords as semitone offsets from the root; one entry per bar. */
  readonly chords: readonly (readonly number[])[];
  readonly pad: OscillatorType;
  readonly padGain: number;
  /** Notes per bar in the melody, or 0 for a pad-only piece. */
  readonly melodySteps: number;
  readonly melodyGain: number;
  /** A pulse on the root, once per beat. Battle music, essentially. */
  readonly bassPulses: number;
  readonly bassGain: number;
}

const MINOR = [0, 3, 7];
const MAJOR = [0, 4, 7];
const shift = (c: readonly number[], by: number): number[] => c.map((n) => n + by);

export const SCORES: Readonly<Record<Scene, Score>> = Object.freeze({
  /** Aeolian, slow, and low: a menu should not be a tune anybody hums. */
  menu: {
    root: "A2", secondsPerBar: 4.4,
    chords: [
      MINOR, shift(MAJOR, -4), shift(MAJOR, 3), shift(MAJOR, -2),
      MINOR, shift(MAJOR, 3), shift(MAJOR, -4), shift(MINOR, 7),
    ],
    pad: "triangle", padGain: 0.035,
    melodySteps: 0, melodyGain: 0,
    bassPulses: 0, bassGain: 0,
  },
  /** Mixolydian and open - the pastoral half of the game. */
  adventure: {
    root: "D3", secondsPerBar: 3.6,
    chords: [
      MAJOR, shift(MAJOR, 5), shift(MAJOR, 7), MAJOR,
      shift(MINOR, 9), shift(MAJOR, 5), shift(MAJOR, -2), MAJOR,
    ],
    pad: "triangle", padGain: 0.030,
    melodySteps: 3, melodyGain: 0.026,
    bassPulses: 0, bassGain: 0,
  },
  /** Warmer and a fifth up: standing in your own hall should sound settled. */
  town: {
    root: "C3", secondsPerBar: 3.2,
    chords: [
      MAJOR, shift(MINOR, 9), shift(MAJOR, 5), shift(MAJOR, 7),
      MAJOR, shift(MAJOR, 5), shift(MINOR, 2), shift(MAJOR, 7),
    ],
    pad: "sine", padGain: 0.034,
    melodySteps: 4, melodyGain: 0.024,
    bassPulses: 0, bassGain: 0,
  },
  /** Dorian, fast, and built on the pulse rather than on the chord. */
  battle: {
    root: "D2", secondsPerBar: 2.0,
    chords: [
      MINOR, MINOR, shift(MAJOR, 10), shift(MAJOR, 8),
      MINOR, shift(MAJOR, 5), shift(MAJOR, 10), MINOR,
    ],
    pad: "sawtooth", padGain: 0.016,
    melodySteps: 0, melodyGain: 0,
    bassPulses: 4, bassGain: 0.042,
  },
});

const hz = (root: string, semis: number): number => note(root) * Math.pow(2, semis / 12);

/**
 * A deterministic walk over the chord tones.
 *
 * `bar * 7 + step * 3` is a coprime stride over a three- or four-note chord, so
 * consecutive notes are never the same and the pattern only repeats when both
 * cycles line up. It is not random and is not trying to be: a melody that used
 * `Math.random` would be different every time a player heard it, which is the
 * one thing a theme must not be.
 */
export function melodyNote(chord: readonly number[], bar: number, step: number): number {
  const i = (bar * 7 + step * 3) % chord.length;
  // The top of the walk lifts an octave every other bar so the line moves.
  const octave = (bar + step) % 4 === 3 ? 12 : 0;
  return (chord[i] ?? 0) + 12 + octave;
}

/**
 * One bar of the score as a patch.
 *
 * The pad is two voices per chord tone, detuned a few cents apart, which is the
 * cheapest way to make three oscillators sound like an instrument instead of
 * three oscillators.
 */
export function bar(score: Score, index: number): Patch {
  const chord = score.chords[index % score.chords.length] ?? [0];
  const len = score.secondsPerBar;
  const voices: Voice[] = [];

  for (const semis of chord) {
    for (const detune of [-6, 6]) {
      voices.push({
        src: score.pad,
        freq: hz(score.root, semis),
        detune,
        gain: score.padGain,
        // The envelope fills the bar and overlaps the next one slightly, which
        // is what stops the bed pumping at the bar line.
        attack: len * 0.35, hold: len * 0.2, decay: len * 0.55,
        filter: { type: "lowpass", freq: 1500 },
      });
    }
  }

  if (score.melodySteps > 0) {
    const step = len / score.melodySteps;
    for (let s = 0; s < score.melodySteps; s++) {
      voices.push({
        src: "triangle",
        freq: hz(score.root, melodyNote(chord, index, s)),
        gain: score.melodyGain,
        attack: 0.02, hold: step * 0.25, decay: step * 0.7,
        delay: s * step,
        filter: { type: "lowpass", freq: 2600 },
      });
    }
  }

  if (score.bassPulses > 0) {
    const step = len / score.bassPulses;
    for (let s = 0; s < score.bassPulses; s++) {
      voices.push({
        src: "square",
        freq: hz(score.root, chord[0] ?? 0),
        gain: score.bassGain,
        attack: 0.004, decay: step * 0.55,
        delay: s * step,
        filter: { type: "lowpass", freq: 420 },
      });
    }
  }

  return { voices };
}
